import { LitElement, html, css, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import "../components/index.js";
import { toast } from "../components/index.js";
import { navigate } from "../nav.js";
import { humanError } from "../api/errors.js";
import {
  listWorkspaces,
  createWorkspace,
  deleteWorkspace,
  type Workspace,
} from "../api/workspaces.js";
import { listTags, type Tag, type TagMatch } from "../api/tags.js";
import { listTeams, roleAtLeast, type Team } from "../api/teams.js";

/**
 * page-workspaces is the /workspaces list. It shows the caller's workspaces as a
 * card grid with a create dialog (name + team select, teams filtered to those
 * where the caller is member+), each card links to /workspaces/:id, and a
 * per-card delete with a confirm dialog. Loading / empty / error are rendered
 * through the component kit.
 *
 * # Why the tag filter is team-scoped
 *
 * The page lists workspaces across EVERY team the caller belongs to, but a tag
 * belongs to exactly one team. A flat union of all teams' tags would be wrong
 * twice over: two teams can each own a "prod" tag with different ids and colors,
 * giving visually identical chips that filter differently — and with the default
 * "all" match, selecting one tag from each team matches nothing at all, since a
 * workspace belongs to one team.
 *
 * So the filter bar leads with a team select, and tag chips appear only once a
 * specific team is chosen. The catalog is then fetched for that team alone,
 * which is also why there is no fan-out of one request per team on load.
 *
 * Card chips need no catalog at all: workspace.tags carries {id,name,color}
 * inline, so a card renders its own team's tags correctly even under "All teams".
 */
@customElement("page-workspaces")
export class PageWorkspaces extends LitElement {
  @state() private loading = true;
  @state() private error = "";
  @state() private workspaces: Workspace[] = [];
  @state() private teams: Team[] = [];

  /** "" means all teams. Tag chips only appear once this names one team. */
  @state() private teamFilter = "";
  @state() private tagFilter: string[] = [];
  @state() private tagMatch: TagMatch = "all";
  /** The selected team's catalog, loaded lazily by loadCatalog. */
  @state() private catalog: Tag[] = [];

  /**
   * Monotonic request id, so a slow response for an earlier filter cannot
   * overwrite a newer one (the guard page-admin-teams.ts uses for its search).
   */
  private listReq = 0;

  @state() private showCreate = false;
  @state() private createName = "";
  @state() private createTeamId = "";
  @state() private creating = false;
  @state() private createError = "";

  @state() private pendingDelete: Workspace | null = null;
  @state() private deleting = false;

  static override styles = css`
    :host {
      display: block;
    }
    .head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head h1 {
      margin: 0;
      font-family: var(--bino-font-sans);
      font-size: 1.375rem;
      font-weight: 600;
      color: var(--bino-text);
    }
    .head .spacer {
      flex: 1;
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
      gap: var(--bino-space-md);
    }
    .card {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-lg);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-lg);
      background: var(--bino-surface);
      box-shadow: var(--bino-shadow-header);
      text-decoration: none;
      color: inherit;
      cursor: pointer;
      transition:
        border-color var(--bino-transition-fast),
        box-shadow var(--bino-transition-fast),
        transform var(--bino-transition-fast);
    }
    .card:hover {
      border-color: var(--bino-border-hover);
      box-shadow: var(--bino-shadow-dropdown);
    }
    .card:focus-visible {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    .name {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
      color: var(--bino-text);
      padding-right: var(--bino-space-xl);
      word-break: break-word;
    }
    .meta {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-xs);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .del {
      position: absolute;
      top: var(--bino-space-sm);
      right: var(--bino-space-sm);
    }
    .filters {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-lg);
    }
    .filter-row {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
    }
    .filter-label {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-muted);
    }
    /* The select rule below is width:100% for the modal's fields; the filter
       selects size to their content instead. */
    select.narrow {
      width: auto;
      min-width: 10rem;
    }
    .tag-row,
    .tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-xs);
    }
    .tag-hint {
      margin: 0;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    select {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    select:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    this.readUrl();
    window.addEventListener("popstate", this.onPopState);
    void this.load();
  }

  override disconnectedCallback() {
    window.removeEventListener("popstate", this.onPopState);
    super.disconnectedCallback();
  }

  private onPopState = () => {
    this.readUrl();
    void this.loadCatalog();
    void this.applyFilter();
  };

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      const [ws, teams] = await Promise.all([
        listWorkspaces({ tags: this.tagFilter, tagMatch: this.tagMatch }),
        listTeams(),
      ]);
      this.workspaces = ws;
      this.teams = teams;
      // A shared link may carry ?tag= without ?team=. Tags belong to one team,
      // so the team is implied — recover it from any matching workspace rather
      // than probing every catalog.
      if (this.teamFilter === "" && this.tagFilter.length > 0) {
        this.teamFilter = ws[0]?.teamId ?? "";
      }
      if (this.teamFilter === "") this.teamFilter = this.defaultTeam();
      await this.loadCatalog();
    } catch (e) {
      this.error = humanError(e);
    } finally {
      this.loading = false;
    }
  }

  /**
   * loadCatalog fetches the selected team's tags. Only one team's catalog is
   * ever needed, because the filter is team-scoped and card chips come embedded
   * in the workspace rows.
   */
  private async loadCatalog() {
    if (!this.teamFilter) {
      this.catalog = [];
      return;
    }
    const teamId = this.teamFilter;
    try {
      const tags = await listTags(teamId);
      if (this.teamFilter === teamId) this.catalog = tags;
    } catch {
      // A missing catalog costs the filter chips, not the listing — leave the
      // page usable rather than replacing it with an error state.
      if (this.teamFilter === teamId) this.catalog = [];
    }
  }

  /** readUrl/syncUrl keep the filter in the query string so it is shareable. */
  private readUrl() {
    const p = new URLSearchParams(location.search);
    this.teamFilter = p.get("team") ?? "";
    this.tagFilter = p.getAll("tag");
    this.tagMatch = p.get("tagMatch") === "any" ? "any" : "all";
    // On the first call teams are not loaded yet, so this is a no-op and load()
    // applies the default instead; on a back/forward it is what keeps a
    // single-team account's chips from vanishing when the URL drops ?team=.
    if (this.teamFilter === "") this.teamFilter = this.defaultTeam();
  }

  /**
   * syncUrl uses replaceState, not pushState: one history entry per chip click
   * makes the back button unusable. The URL still survives a reload and can be
   * shared. nav.navigate() is deliberately not used — the pathname is unchanged,
   * so there is nothing for the router to re-resolve.
   */
  private syncUrl() {
    const q = new URLSearchParams();
    if (this.teamFilter) q.set("team", this.teamFilter);
    for (const id of this.tagFilter) q.append("tag", id);
    if (this.tagMatch === "any") q.set("tagMatch", "any");
    const s = q.toString();
    history.replaceState({}, "", location.pathname + (s ? `?${s}` : ""));
  }

  private async applyFilter() {
    this.syncUrl();
    const req = ++this.listReq;
    try {
      const ws = await listWorkspaces({ tags: this.tagFilter, tagMatch: this.tagMatch });
      if (req !== this.listReq) return; // a newer toggle already won
      this.workspaces = ws;
    } catch (e) {
      if (req === this.listReq) toast(humanError(e), "error");
    }
  }

  private onTeamChange(teamId: string) {
    this.teamFilter = teamId;
    // Changing team always clears the tag selection: a tag belongs to exactly
    // one team, so no previously selected tag can apply to the new one — and
    // under the default "all" match a leftover would silently match nothing.
    this.tagFilter = [];
    this.catalog = [];
    void this.loadCatalog();
    void this.applyFilter();
  }

  private toggleTag(id: string) {
    this.tagFilter = this.tagFilter.includes(id)
      ? this.tagFilter.filter((x) => x !== id)
      : [...this.tagFilter, id];
    void this.applyFilter();
  }

  /**
   * defaultTeam is the team the filter starts on. With exactly one team there is
   * no ambiguity about whose tags to offer, so it is scoped to that team up
   * front — without this a single-team account would never see the tag chips at
   * all, since they only appear once a team is chosen and the "All teams"
   * default would never resolve to one.
   */
  private defaultTeam(): string {
    return this.teams.length === 1 ? (this.teams[0]?.id ?? "") : "";
  }

  private clearFilters() {
    this.teamFilter = this.defaultTeam();
    this.tagFilter = [];
    this.tagMatch = "all";
    this.catalog = [];
    void this.loadCatalog();
    void this.applyFilter();
  }

  /** Narrowed beyond the default — which is what "Clear" undoes. */
  private get filterActive(): boolean {
    return this.tagFilter.length > 0 || this.teamFilter !== this.defaultTeam();
  }

  /**
   * The rows on screen. The server applied the tag filter; the team narrowing is
   * local because /v1/workspaces takes no team parameter and teamId is already
   * on every row.
   */
  private get visible(): Workspace[] {
    return this.teamFilter === ""
      ? this.workspaces
      : this.workspaces.filter((w) => w.teamId === this.teamFilter);
  }

  private get writableTeams(): Team[] {
    return this.teams.filter((t) => roleAtLeast(t.role, "member"));
  }

  private teamName(id: string): string {
    const t = this.teams.find((x) => x.id === id);
    return t ? t.displayName || t.name : id;
  }

  private openCreate() {
    this.createName = "";
    this.createError = "";
    this.createTeamId = this.writableTeams[0]?.id ?? "";
    this.showCreate = true;
  }

  private async submitCreate() {
    const name = this.createName.trim();
    if (!name) {
      this.createError = "Enter a workspace name.";
      return;
    }
    if (!this.createTeamId) {
      this.createError = "Select a team.";
      return;
    }
    this.creating = true;
    this.createError = "";
    try {
      const ws = await createWorkspace(this.createTeamId, name);
      toast("Workspace created", "success");
      this.showCreate = false;
      navigate(`/workspaces/${ws.id}`);
    } catch (e) {
      this.createError = humanError(e);
    } finally {
      this.creating = false;
    }
  }

  private async confirmDelete() {
    if (!this.pendingDelete) return;
    this.deleting = true;
    try {
      const gone = this.pendingDelete;
      await deleteWorkspace(gone.id);
      // A default-branch workspace takes its repository's other branch
      // workspaces with it.
      const repo = gone.defaultBranch ? gone.repository : undefined;
      this.workspaces = this.workspaces.filter(
        (w) => w.id !== gone.id && !(repo && w.repository === repo),
      );
      toast("Workspace deleted", "success");
      this.pendingDelete = null;
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.deleting = false;
    }
  }

  /**
   * The filter bar renders only when there is something to filter, so an account
   * on a single team with no tags never sees new chrome.
   */
  private renderFilters() {
    if (this.loading || this.error) return nothing;
    const multiTeam = this.teams.length > 1;
    if (!multiTeam && this.catalog.length === 0 && !this.filterActive) return nothing;

    const team = this.teams.find((t) => t.id === this.teamFilter);
    return html`<div class="filters">
      <div class="filter-row">
        ${
          // With one team the select can only pick what is already picked, so it
          // is noise — the tag chips below are the whole filter.
          multiTeam
            ? html`<label class="filter-label" for="team-filter">Team</label>
                <select
                  id="team-filter"
                  class="narrow"
                  .value=${this.teamFilter}
                  @change=${(e: Event) => this.onTeamChange((e.target as HTMLSelectElement).value)}
                >
                  <option value="" ?selected=${this.teamFilter === ""}>All teams</option>
                  ${this.teams.map(
                    (t) => html`<option value=${t.id} ?selected=${t.id === this.teamFilter}>
                      ${t.displayName || t.name}
                    </option>`,
                  )}
                </select>`
            : nothing
        }
        ${
          // Meaningless with fewer than two tags selected, so it stays hidden.
          this.tagFilter.length > 1
            ? html`<select
                class="narrow"
                aria-label="Tag match mode"
                .value=${this.tagMatch}
                @change=${(e: Event) => {
                  this.tagMatch = (e.target as HTMLSelectElement).value as TagMatch;
                  void this.applyFilter();
                }}
              >
                <option value="all" ?selected=${this.tagMatch === "all"}>
                  with all of these tags
                </option>
                <option value="any" ?selected=${this.tagMatch === "any"}>
                  with any of these tags
                </option>
              </select>`
            : nothing
        }
        ${this.filterActive
          ? html`<bn-button variant="ghost" size="sm" @click=${() => this.clearFilters()}
              >Clear</bn-button
            >`
          : nothing}
      </div>
      ${team
        ? this.catalog.length > 0
          ? html`<div
              class="tag-row"
              role="group"
              aria-label=${`Filter by ${team.displayName || team.name} tags`}
            >
              ${this.catalog.map(
                (t) => html`<bn-tag
                  selectable
                  .name=${t.name}
                  color=${t.color}
                  ?selected=${this.tagFilter.includes(t.id)}
                  @toggle=${() => this.toggleTag(t.id)}
                ></bn-tag>`,
              )}
            </div>`
          : html`<p class="tag-hint">
              ${team.displayName || team.name} has no tags yet. Add them on the team page.
            </p>`
        : html`<p class="tag-hint">Pick a team to filter by its tags. Each tag belongs to one team.</p>`}
    </div>`;
  }

  private renderBody() {
    if (this.loading) return html`<bn-spinner block label="Loading workspaces…"></bn-spinner>`;
    if (this.error) {
      return html`<bn-empty-state icon="⚠" heading="Couldn’t load workspaces" message=${this.error}>
        <bn-button slot="actions" variant="secondary" @click=${() => this.load()}>Retry</bn-button>
      </bn-empty-state>`;
    }
    const rows = this.visible;
    if (rows.length === 0) {
      // Two different nothings: an account with no workspaces needs a create
      // button, a filter that matched nothing needs a way back out.
      if (this.filterActive) {
        return html`<bn-empty-state
          icon="⌕"
          heading="No workspaces match"
          message=${this.tagFilter.length > 0
            ? `No workspace carries ${this.tagMatch === "all" ? "all" : "any"} of the selected tags.`
            : "That team has no workspaces yet."}
        >
          <bn-button slot="actions" variant="secondary" @click=${() => this.clearFilters()}
            >Clear filters</bn-button
          >
        </bn-empty-state>`;
      }
      return html`<bn-empty-state
        icon="◲"
        heading="No workspaces yet"
        message="Create a workspace to build your first report."
      >
        <bn-button
          slot="actions"
          variant="primary"
          ?disabled=${this.writableTeams.length === 0}
          @click=${() => this.openCreate()}
          >New workspace</bn-button
        >
      </bn-empty-state>`;
    }
    return html`<div class="grid">
      ${rows.map(
        (w) => html`<a
          class="card"
          href=${`/workspaces/${w.id}`}
          @click=${(e: Event) => {
            e.preventDefault();
            navigate(`/workspaces/${w.id}`);
          }}
        >
          <span class="name">${w.name}</span>
          <span class="meta">
            <span>${this.teamName(w.teamId)}</span>
            <span>Updated ${new Date(w.updated).toLocaleString()}</span>
          </span>
          ${w.tags.length > 0
            ? html`<span class="tags">
                ${w.tags.map(
                  (t) => html`<bn-tag .name=${t.name} color=${t.color}></bn-tag>`,
                )}
              </span>`
            : nothing}
          <bn-button
            class="del"
            variant="ghost"
            size="sm"
            title="Delete workspace"
            @click=${(e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              this.pendingDelete = w;
            }}
            >Delete</bn-button
          >
        </a>`,
      )}
    </div>`;
  }

  override render() {
    return html`
      <div class="head">
        <h1>Workspaces</h1>
        <span class="spacer"></span>
        <bn-button
          variant="primary"
          ?disabled=${this.loading || this.writableTeams.length === 0}
          @click=${() => this.openCreate()}
          >New workspace</bn-button
        >
      </div>

      ${this.renderFilters()} ${this.renderBody()}

      <bn-modal
        ?open=${this.showCreate}
        heading="New workspace"
        @close=${() => (this.showCreate = false)}
      >
        <bn-field label="Name" required error=${this.createError}>
          <bn-input
            .value=${this.createName}
            placeholder="my-report"
            @input=${(e: CustomEvent<{ value: string }>) => (this.createName = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Team" required>
          <select
            .value=${this.createTeamId}
            @change=${(e: Event) => (this.createTeamId = (e.target as HTMLSelectElement).value)}
          >
            ${this.writableTeams.map(
              (t) => html`<option value=${t.id} ?selected=${t.id === this.createTeamId}>
                ${t.displayName || t.name}
              </option>`,
            )}
          </select>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showCreate = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.creating}
          @click=${() => this.submitCreate()}
          >Create</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingDelete !== null}
        heading="Delete workspace?"
        @close=${() => (this.pendingDelete = null)}
      >
        ${this.pendingDelete?.defaultBranch
          ? html`<p>
              Delete <strong>${this.pendingDelete.name}</strong>? It holds the default branch of its
              repository, so the repository is deleted too: all its branch workspaces, its git
              history, its data drops and seals, and its backup. Files, secrets, and builds are
              removed permanently. This cannot be undone.
            </p>`
          : this.pendingDelete?.repository
            ? html`<p>
                Delete <strong>${this.pendingDelete.name}</strong>? Its secrets and builds are
                removed. The branch stays in the repository, and its next change brings the
                workspace back.
              </p>`
            : this.pendingDelete
              ? html`<p>
                  Delete <strong>${this.pendingDelete.name}</strong>? Its files, secrets, and
                  builds are removed permanently. This cannot be undone.
                </p>`
              : nothing}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingDelete = null)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.deleting}
          @click=${() => this.confirmDelete()}
          >Delete</bn-button
        >
      </bn-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-workspaces": PageWorkspaces;
  }
}
